/**
 * Ferry crossings: schedules, truck rules, and the maths that turns "this
 * route uses the ferry" into "be at the pier by 12:40 for the 13:40 sailing".
 *
 * There is no public API for Thai ferry schedules, so the timetables live
 * here as editable defaults: the published times are pre-filled and Settings
 * > Ferries lets any driver correct them when the operator changes them.
 * Times, crossing minutes and the truck flag persist in localStorage; pier
 * coordinates stay in code (they don't move).
 *
 * The rule set, as specified by the admin (2026-07-29):
 *  - EVERY vehicle plans to be at the pier one hour before the sailing.
 *  - The big truck may only board operators marked truckOk (e.g. Raja Ferry
 *    on the Samui run - Seatran doesn't take it).
 *  - The wait at the pier goes INTO the shown ETA, so comparing route options
 *    stays honest: a "faster" road that misses the sailing is not faster.
 *
 * ETA note: the route's own duration already contains the router's estimate
 * of the water leg, so only the WAIT (pier arrival -> departure) is added on
 * top; adding our crossing minutes as well would count the water twice.
 */

import type { LngLat, Route } from '../core/types';
import { distance } from '../core/geo';

export interface FerryOperator {
  id: string;
  name: string;
  /** May this operator carry the big truck? Editable in Settings. */
  truckOk: boolean;
  /** Daily departures "HH:MM", mainland -> island. */
  outbound: string[];
  /** Daily departures "HH:MM", island -> mainland. */
  inbound: string[];
  /** Minutes on the water. */
  crossingMinutes: number;
}

export interface FerryCrossing {
  id: string;
  name: string;
  pierAName: string;
  pierBName: string;
  /** Mainland-side pier point(s); several when operators use adjacent piers. */
  piersA: LngLat[];
  /** Island-side pier point(s). */
  piersB: LngLat[];
  operators: FerryOperator[];
}

/** Everyone plans to be at the pier this long before the sailing. */
export const PIER_BUFFER_MINUTES = 60;

/** How close the route geometry must pass to a pier to count as using it. */
const MATCH_RADIUS_M = 3000;

const KEY = 'wayfarer.ferries.v1';

/*
 * Published schedules as of July 2026 - starting points, not gospel; edit in
 * Settings when the operator changes them. Koh Chang's Center Point service
 * has been suspended since mid-2024, so one operator remains there.
 */
export const DEFAULT_FERRIES: FerryCrossing[] = [
  {
    id: 'kohchang',
    name: 'Koh Chang',
    pierAName: 'Ao Thammachat',
    pierBName: 'Ao Sapparot',
    piersA: [[102.3013, 12.1871]],
    piersB: [[102.2807, 12.1417]],
    operators: [
      {
        id: 'kohchang-ferry',
        name: 'Koh Chang Ferry',
        truckOk: true,
        outbound: ['06:30', '07:30', '08:30', '09:30', '10:30', '11:30', '12:30', '13:30', '14:30', '15:30', '16:30', '17:30', '18:30'],
        inbound: ['06:30', '07:30', '08:30', '09:30', '10:30', '11:30', '12:30', '13:30', '14:30', '15:30', '16:30', '17:30', '18:30'],
        crossingMinutes: 35,
      },
    ],
  },
  {
    id: 'samui',
    name: 'Koh Samui',
    pierAName: 'Donsak',
    pierBName: 'Samui',
    piersA: [
      [99.6935, 9.1607], // Raja pier
      [99.7017, 9.1745], // Seatran pier
    ],
    piersB: [
      [99.9128, 9.4866], // Lipa Noi (Raja)
      [99.932, 9.5399], // Nathon (Seatran)
    ],
    operators: [
      {
        id: 'raja',
        name: 'Raja Ferry',
        truckOk: true,
        outbound: ['05:00', '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00'],
        inbound: ['05:00', '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00'],
        crossingMinutes: 90,
      },
      {
        id: 'seatran',
        name: 'Seatran',
        truckOk: false,
        outbound: ['05:00', '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00'],
        inbound: ['05:00', '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00'],
        crossingMinutes: 90,
      },
    ],
  },
];

/** The editable half of an operator, as persisted. */
interface StoredOperator {
  truckOk?: boolean;
  outbound?: string[];
  inbound?: string[];
  crossingMinutes?: number;
}

/**
 * Stored edits are laid OVER the defaults rather than replacing them, so a
 * future app version can add a crossing without old installs hiding it.
 */
export function loadFerries(): FerryCrossing[] {
  let stored: Record<string, Record<string, StoredOperator>> = {};
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) stored = JSON.parse(raw);
  } catch {
    /* corrupt - defaults win */
  }
  return DEFAULT_FERRIES.map((c) => ({
    ...c,
    operators: c.operators.map((op) => {
      const s = stored?.[c.id]?.[op.id];
      if (!s) return { ...op };
      return {
        ...op,
        truckOk: typeof s.truckOk === 'boolean' ? s.truckOk : op.truckOk,
        outbound: validTimes(s.outbound) ?? op.outbound,
        inbound: validTimes(s.inbound) ?? op.inbound,
        crossingMinutes:
          typeof s.crossingMinutes === 'number' && s.crossingMinutes > 0
            ? s.crossingMinutes
            : op.crossingMinutes,
      };
    }),
  }));
}

export function saveFerries(ferries: FerryCrossing[]): void {
  const out: Record<string, Record<string, StoredOperator>> = {};
  for (const c of ferries) {
    out[c.id] = {};
    for (const op of c.operators) {
      out[c.id][op.id] = {
        truckOk: op.truckOk,
        outbound: op.outbound,
        inbound: op.inbound,
        crossingMinutes: op.crossingMinutes,
      };
    }
  }
  try {
    localStorage.setItem(KEY, JSON.stringify(out));
  } catch {
    /* quota / private mode - edits just won't persist */
  }
}

/** Parse "06:30, 7:30 08:30" into sorted valid "HH:MM"s; null if none. */
export function parseTimes(text: string): string[] | null {
  const times = text
    .split(/[,;\s]+/)
    .map((t) => t.trim())
    .filter(Boolean)
    .map((t) => {
      const m = t.match(/^(\d{1,2}):(\d{2})$/);
      if (!m) return null;
      const h = Number(m[1]);
      const min = Number(m[2]);
      if (h > 23 || min > 59) return null;
      return `${String(h).padStart(2, '0')}:${m[2]}`;
    });
  const valid = times.filter((t): t is string => t !== null);
  return valid.length ? [...new Set(valid)].sort() : null;
}

function validTimes(list: unknown): string[] | null {
  if (!Array.isArray(list)) return null;
  const ok = list.filter((t) => typeof t === 'string' && /^\d{2}:\d{2}$/.test(t));
  return ok.length ? (ok as string[]) : null;
}

export interface FerryPlan {
  crossingId: string;
  crossingName: string;
  /** Which pier this journey departs from. */
  pierName: string;
  /** null when no configured operator may carry this vehicle. */
  operator: FerryOperator | null;
  direction: 'outbound' | 'inbound';
  /** When we expect to reach the pier, epoch ms. */
  pierEta: number;
  /** The chosen sailing, epoch ms. 0 when operator is null. */
  departureAt: number;
  /** departureAt minus the 1 h buffer - "be there by". */
  beAtPierBy: number;
  /** Pier arrival -> sailing, seconds; added to the route's shown ETA. */
  waitSeconds: number;
  /** True when today's last sailing is missed and this is tomorrow's first. */
  nextDay: boolean;
}

/**
 * Detect a configured crossing on a route and pick the sailing.
 *
 * Detection is geometric - the route must pass within MATCH_RADIUS_M of a
 * pier on BOTH sides - so it works whatever the router calls the ferry leg.
 * The chosen sailing is the earliest departure at least one hour after the
 * predicted pier arrival, across every operator this vehicle may board.
 */
export function planFerry(
  route: Route,
  totalDurationSeconds: number,
  now: number,
  isTruck: boolean,
  ferries: FerryCrossing[] = loadFerries(),
): FerryPlan | null {
  const coords = route.geometry.coordinates as LngLat[];
  if (coords.length < 2) return null;

  for (const crossing of ferries) {
    const iA = firstIndexNear(coords, crossing.piersA);
    if (iA < 0) continue;
    const iB = firstIndexNear(coords, crossing.piersB);
    if (iB < 0) continue;

    const direction: FerryPlan['direction'] = iA < iB ? 'outbound' : 'inbound';
    const pierIdx = Math.min(iA, iB);
    const pierName = direction === 'outbound' ? crossing.pierAName : crossing.pierBName;

    // Drive time to the pier: the distance fraction of the paced duration.
    const total = lineLength(coords, coords.length - 1);
    const toPier = lineLength(coords, pierIdx);
    const pierEta = now + (total > 0 ? (toPier / total) * totalDurationSeconds : 0) * 1000;

    const candidates = crossing.operators.filter((op) => !isTruck || op.truckOk);
    if (!candidates.length) {
      return {
        crossingId: crossing.id,
        crossingName: crossing.name,
        pierName,
        operator: null,
        direction,
        pierEta,
        departureAt: 0,
        beAtPierBy: 0,
        waitSeconds: 0,
        nextDay: false,
      };
    }

    const earliest = pierEta + PIER_BUFFER_MINUTES * 60_000;
    let best: { op: FerryOperator; at: number; nextDay: boolean } | null = null;
    for (const op of candidates) {
      const times = direction === 'outbound' ? op.outbound : op.inbound;
      const hit = nextDeparture(times, earliest);
      if (hit && (!best || hit.at < best.at)) best = { op, ...hit };
    }
    if (!best) return null; // no times configured at all

    return {
      crossingId: crossing.id,
      crossingName: crossing.name,
      pierName,
      operator: best.op,
      direction,
      pierEta,
      departureAt: best.at,
      beAtPierBy: best.at - PIER_BUFFER_MINUTES * 60_000,
      waitSeconds: Math.max(0, Math.round((best.at - pierEta) / 1000)),
      nextDay: best.nextDay,
    };
  }
  return null;
}

/** First coordinate index within MATCH_RADIUS_M of any of the piers, or -1. */
function firstIndexNear(coords: LngLat[], piers: LngLat[]): number {
  for (let i = 0; i < coords.length; i++) {
    for (const p of piers) {
      if (distance(coords[i], p) <= MATCH_RADIUS_M) return i;
    }
  }
  return -1;
}

/** Metres along the line up to (and including) index `end`. */
function lineLength(coords: LngLat[], end: number): number {
  let m = 0;
  for (let i = 1; i <= end && i < coords.length; i++) m += distance(coords[i - 1], coords[i]);
  return m;
}

/** Earliest daily departure at or after `earliest` (epoch ms); rolls to tomorrow. */
function nextDeparture(times: string[], earliest: number): { at: number; nextDay: boolean } | null {
  if (!times.length) return null;
  const base = new Date(earliest);
  for (const dayOffset of [0, 1]) {
    for (const t of [...times].sort()) {
      const [h, min] = t.split(':').map(Number);
      const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + dayOffset, h, min);
      if (d.getTime() >= earliest) return { at: d.getTime(), nextDay: dayOffset > 0 };
    }
  }
  return null;
}
